import React from 'react';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/system';

import { toCssHeight, RING, ringMetrics } from '../../facade';

// React twin of the facade loader ring (`buildBookingLoader` in src/facade.js):
// the facade paints a pure-CSS ring before React exists; this is the version shown
// once the app mounts (App Suspense fallback, lazy calendar fallbacks). Both derive
// their geometry/colour/motion from the shared RING/ringMetrics in facade.js, so the
// facade → app handoff can't drift — only the rendering (CSS string vs MUI sx) differs.
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Visually-hidden, but announced by screen readers — mirrors the facade's
// `.tmk-sr` span so the loading state keeps an accessible name (the "no visible
// captions" rule only forbids *visible* text).
const srOnly = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export default function BookingSpinner({
  height,
  size,
  color = RING.color,
  label = 'Loading',
}) {
  const { px, border } = ringMetrics(size);

  return (
    <Box
      role="status"
      sx={{
        boxSizing: 'border-box',
        minHeight: toCssHeight(height),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1,
      }}
    >
      <Box
        sx={{
          width: px,
          height: px,
          borderRadius: '50%',
          border: `${border}px solid ${RING.trackLight}`,
          borderTopColor: color,
          animation: `${spin} ${RING.duration} linear infinite`,
          '@media (prefers-reduced-motion:reduce)': {
            animation: 'none',
            borderColor: color,
            opacity: 0.5,
          },
          '@media (prefers-color-scheme:dark)': {
            borderColor: RING.trackDark,
            borderTopColor: color,
          },
        }}
      />
      <Box component="span" sx={srOnly}>
        {label}
      </Box>
    </Box>
  );
}
